import { Component, inject, Input, Signal, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TableModule, TableRowReorderEvent } from "@openng/optimus-ui/table";

import { SvgIconComponent } from "../../../../shared/components/svg-icon/svg-icon.component";
import { FormsModule } from "@angular/forms";
import { InputTextModule } from "@openng/optimus-ui/inputtext";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { ButlerService } from "../../../../services/api/butler/butler.service";
import { NgClass } from "@angular/common";
import { DragDropModule } from "@openng/optimus-ui/dragdrop";
import { Definition, Instruction, Nodes } from "../../../../models/api/ruleChain";
import { DropdownOption } from "../../../../models/ui/dropdownOption";
import { Column } from "../../../../models/ui/tableColumn";
import { first } from "rxjs";
import { SelectModule } from "@openng/optimus-ui/select";

@Component({
  selector: "app-instruction-table",
  standalone: true,
  imports: [
    DragDropModule,
    TableModule,
    TranslateModule,
    SvgIconComponent,
    SelectModule,
    InputTextModule,
    FormsModule,
    ButtonComponent,
    NgClass,
  ],
  templateUrl: "./instruction-table.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./instruction-table.component.scss",
})
export class InstructionTableComponent {
  @Input() instruction!: WritableSignal<Instruction>;
  @Input() definitions!: WritableSignal<Definition[]>;
  private translate = inject(TranslateService);
  private butlerService = inject(ButlerService);

  public instructionColumns: Signal<Column[]> = signal([
    { field: "logic", header: "butler_programming.instructions.logic" },
    {
      field: "definition",
      subField: "label",
      header: "butler_programming.instructions.definition",
    },

    {
      field: "notes",
      header: "butler_programming.instructions.description",
    },
    { field: "value", header: "butler_programming.instructions.value" },
  ]);

  public logicOptions: Signal<DropdownOption[]> = signal([
    {
      label: this.translate.instant("butler_programming.instructions.logicOptions.IF"),
      value: "IF",
    },

    {
      label: this.translate.instant("butler_programming.instructions.logicOptions.AND"),
      value: "AND",
    },
    {
      label: this.translate.instant("butler_programming.instructions.logicOptions.OR"),
      value: "OR",
    },
    {
      label: this.translate.instant("butler_programming.instructions.logicOptions.AND NOT"),
      value: "AND NOT",
    },
    {
      label: this.translate.instant("butler_programming.instructions.logicOptions.OR NOT"),
      value: "OR NOT",
    },
    {
      label: this.translate.instant("butler_programming.instructions.logicOptions.THEN"),
      value: "THEN",
    },
  ]);
  public flagOptions: Signal<DropdownOption[]> = signal([
    { label: "butler_programming.valueOptions.true", value: true },
    { label: "butler_programming.valueOptions.false", value: false },
  ]);
  public relayOptions: Signal<DropdownOption[]> = signal([
    { label: "butler_programming.relayValueOptions.true", value: true },
    { label: "butler_programming.relayValueOptions.false", value: false },
  ]);

  public editingRowKeys: Record<string, boolean> = {};
  public clonedNodes: Record<string, Nodes> = {};

  /**
   * Saves the current instruction by calling the editInstruction service method.
   * Subscribes to the result to handle the response.
   */
  public saveInstructions() {
    this.butlerService.editInstruction(this.instruction()).pipe(first()).subscribe();
  }

  /**
   * Reorders the instruction array based on a drag/drop event.
   * Also updates the editingRowKeys state to reflect the new order.
   *
   * @param event - The TableRowReorderEvent from the PrimeNG table.
   */
  public onRowReorder(event: TableRowReorderEvent) {
    const dragIndex = event.dragIndex;
    const dropIndex = event.dropIndex;
    if (dragIndex !== undefined && dropIndex !== undefined) {
      // Step 1: Update position properties in the reordered array
      this.instruction.update((current: Instruction) => {
        const nodes = current.nodes;
        return {
          ...current,
          nodes: nodes.map((node, index) => {
            node.position = index;

            return node;
          }),
        };
      });
      // Step 2: Create a new state object based on the drag/drop operation
      const updatedState: Record<number, boolean> = {};

      // Process each index in the original state
      Object.entries(this.editingRowKeys).forEach(([indexStr, isEditable]) => {
        const originalIndex = parseInt(indexStr);
        let newIndex: number;

        if (originalIndex === dragIndex) {
          // The dragged item moves to the drop position
          newIndex = dropIndex;
        } else if (dragIndex < dropIndex) {
          // Dragging forward: items between dragIndex and dropIndex shift backward
          if (originalIndex > dragIndex && originalIndex <= dropIndex) {
            newIndex = originalIndex - 1;
          } else {
            newIndex = originalIndex;
          }
        } else {
          // Dragging backward: items between dropIndex and dragIndex shift forward
          if (originalIndex >= dropIndex && originalIndex < dragIndex) {
            newIndex = originalIndex + 1;
          } else {
            newIndex = originalIndex;
          }
        }

        // Only add to the new state if the value is true (or you can keep false values too)
        if (isEditable) {
          updatedState[newIndex] = isEditable;
        }
      });

      this.editingRowKeys = updatedState;
    }
  }

  /**
   * Move instruction up in the list and update editingRowKeys state.
   * @param rowIndex - Index of the instruction to move up.
   */
  public onRowUp(rowIndex: number) {
    this.instruction.update((instruction: Instruction) => {
      if (instruction && instruction.nodes) {
        const node = instruction.nodes[rowIndex];

        instruction.nodes[rowIndex] = instruction.nodes[rowIndex - 1];
        instruction.nodes[rowIndex - 1] = node;
      }
      return {
        ...instruction,
        nodes: instruction.nodes.map((node, index) => {
          node.position = index;
          return node;
        }),
      };
    });
    const prevRowEditing = this.editingRowKeys[rowIndex - 1];
    const currRowEditing = this.editingRowKeys[rowIndex];

    this.editingRowKeys[rowIndex] = prevRowEditing;
    this.editingRowKeys[rowIndex - 1] = currRowEditing;
  }

  /**
   * Move instruction down in the list and update editingRowKeys state.
   * @param rowIndex - Index of the instruction to move down.
   */
  public onRowDown(rowIndex: number) {
    this.instruction.update((instruction: Instruction) => {
      if (instruction && instruction.nodes) {
        const node = instruction.nodes[rowIndex];

        instruction.nodes[rowIndex] = instruction.nodes[rowIndex + 1];
        instruction.nodes[rowIndex + 1] = node;
      }
      return {
        ...instruction,
        nodes: instruction.nodes.map((node, index) => {
          node.position = index;
          return node;
        }),
      };
    });
    const nextRowEditing = this.editingRowKeys[rowIndex + 1];
    const currRowEditing = this.editingRowKeys[rowIndex];

    this.editingRowKeys[rowIndex] = nextRowEditing;
    this.editingRowKeys[rowIndex + 1] = currRowEditing;
  }

  /**
   * Adds a new instruction to the instruction list and sets the editing flag
   * for the newly added instruction to true.
   */
  public addInstruction() {
    if (this.definitions().length > 0) {
      this.instruction.update((instruction: Instruction) => {
        const definition = this.definitions()[0];
        if (instruction && instruction.nodes && definition.id) {
          const position = this.findPosition(instruction.nodes);
          this.editingRowKeys[position.toString()] = true;
          instruction.nodes.push({
            position: position,
            notes: null,
            definition: {
              id: definition.id,
              label: definition.label,
              type: definition.type,
            },
          });
        }
        return instruction;
      });
    }
  }

  /**
   * Finds the greatest position of all nodes in the instruction list and returns it
   * plus one. If the instruction list is empty, returns 0.
   * @param nodes - The instruction list.
   * @returns The greatest position found plus one.
   */
  private findPosition(nodes: Nodes[]) {
    let greatestPosition = 0;
    nodes.forEach((node) => {
      if (node.position !== undefined && Number(node.position) > greatestPosition) {
        greatestPosition = Number(node.position);
      }
    });
    return nodes.length > 0 ? greatestPosition + 1 : 0;
  }

  /**
   * Saves a copy of the node in clonedNodes before editing is initiated
   * @param node - Node to be edited
   */
  public onRowEditInit(node: Nodes) {
    if (node.position !== undefined) {
      this.clonedNodes[node.position.toString()] = { ...node };
    }
  }

  /**
   * Deletes a node from the instruction list. Also updates the position of other nodes so
   * that there is no gap in the positions.
   * @param node - Node to be deleted.
   */
  public onRowDelete(node: Nodes) {
    if (node.position !== undefined) {
      this.instruction.update((instruction: Instruction) => {
        if (instruction && instruction.nodes) {
          const positionNodeClicked = node.position;
          const indexToDelete = instruction.nodes.findIndex((item) => item.position === positionNodeClicked);
          if (indexToDelete > -1) {
            // change position attribute for nodes after the deleted one, only if it's not the last one
            if (indexToDelete < instruction.nodes.length - 1) {
              for (let i = indexToDelete + 1; i < instruction.nodes.length; i++) {
                const newPosition = Number(instruction.nodes[i].position) - 1;
                if (newPosition >= 0) {
                  instruction.nodes[i].position = newPosition;
                }
              }
            }

            // delete the node
            instruction.nodes.splice(indexToDelete, 1);
          }
        }
        return instruction;
      });
    }
  }
  /**
   * Called when the user clicks the save button to save a node edit.
   * Deletes the cloned node from clonedNodes.
   * @param node - The edited node.
   */
  public onRowEditSave(node: Nodes) {
    if (node.position) {
      delete this.clonedNodes[node.position?.toString()];
    }
  }

  /**
   * Cancels the edit operation for a node and restores its original state.
   * If the node's original state was cloned, it replaces the current node with the cloned version.
   * @param node - The node for which the edit operation is being canceled.
   */
  public onRowEditCancel(node: Nodes) {
    this.instruction.update((instruction: Instruction) => {
      if (instruction && instruction.nodes) {
        const position = node.position;

        if (position !== undefined && this.clonedNodes[position.toString()]) {
          instruction.nodes[position] = this.clonedNodes[position.toString()];
        } else {
          instruction.nodes.pop();
        }
      }
      return instruction;
    });
    if (node.position) {
      delete this.clonedNodes[node.position?.toString()];
    }
  }

  /**
   * Checks if the node at the given row index is a "THEN" node and its next node is not a "THEN" node.
   * @param rowIndex - The index of the node to check.
   * @param rowData - The data of the node to check.
   * @returns True if the node is a "THEN" node and its next node is not a "THEN" node, false otherwise.
   */
  public checkIfThenLine(rowIndex: number, rowData: Nodes) {
    const next = this.instruction().nodes[rowIndex + 1];
    return this.isThenNode(rowData) && !this.isThenNode(next);
  }

  public isThenNode(node: Nodes | undefined) {
    const logic = String(node?.logic ?? "")
      .trim()
      .toLowerCase();
    return logic === "then";
  }

  public isFlagDefinitionNode(node: Nodes) {
    const type = typeof node?.definition?.type === "string" ? node.definition.type : "";
    return type.trim().toLowerCase() === "flag";
  }

  public valueOptionsForNode(node: Nodes): DropdownOption[] {
    return this.isFlagDefinitionNode(node) ? this.flagOptions() : this.relayOptions();
  }

  public valueLabelKeyForNode(node: Nodes, value: boolean): string {
    const base = this.isFlagDefinitionNode(node)
      ? "butler_programming.valueOptions"
      : "butler_programming.relayValueOptions";
    return `${base}.${value}`;
  }
}
